import { useState, type ReactNode } from 'react';
import type { DocumentArtifact } from '@/domain/schema/v1';
import {
  columnWidths,
  isHeaderCell,
  isDialogueExtraField,
  isDialogueMainField,
  isDialogueRangeNote,
  isVerbatimLabel,
  parseDoc,
  parseVerbatimLine,
  splitTables,
  type DocBlock,
} from '@/domain/workflow/docModel';

const FLAG = /^\[(자료 부족|상담자 직접 작성 필요)/;
const AI_MARK = /^\[AI 초안 — (확인 필요|검토 후 수정하세요)\]$/;

/** 양식 표: 칸 수가 모자란 줄은 마지막 칸을 늘린다. 칸 수가 2인 줄의 첫 칸·표 둘째 줄까지는 머리 칸 */
function FormTable({ rows }: { rows: string[][] }) {
  const cols = Math.max(...rows.map((r) => r.length));
  const widths = columnWidths(rows);
  return (
    <table className="form-table data-table">
      <colgroup>
        {widths.map((w, j) => (
          <col key={j} style={{ width: `${w}%` }} />
        ))}
      </colgroup>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => {
              const span = j === r.length - 1 ? cols - r.length + 1 : 1;
              const head = isHeaderCell(rows, i, j);
              return head ? (
                <th key={j} colSpan={span}>
                  {c}
                </th>
              ) : (
                <td key={j} colSpan={span}>
                  {c}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Lines({ lines }: { lines: string[] }) {
  return (
    <>
      {splitTables(lines).map((part, k) =>
        part.kind === 'table' ? <FormTable key={k} rows={part.rows} /> : <PlainLines key={k} lines={part.lines} />,
      )}
    </>
  );
}

function PlainLines({ lines }: { lines: string[] }) {
  return (
    <>
      {lines.map((l, i) =>
        l.trim() ? (
          <p
            key={i}
            className={
              AI_MARK.test(l.trim()) ? 'doc-ai' : FLAG.test(l) ? 'doc-flag' : /^-\s/.test(l) ? 'doc-bullet' : undefined
            }
          >
            {/^-\s/.test(l) ? l.replace(/^-\s+/, '') : l}
          </p>
        ) : null,
      )}
    </>
  );
}

/** 축어록: "P1 발언" 문단, 상담자 발언 앞에 한 줄 띄움 (대화록 양식) */
function VerbatimDialogue({ lines }: { lines: string[] }) {
  const rows = lines.filter((l) => l.trim()).map((l) => ({ l, v: parseVerbatimLine(l) }));
  const firstIdx = rows.findIndex((r) => r.v);
  return (
    <div className="verbatim">
      {rows.map(({ l, v }, i) => {
        if (!v)
          return (
            <p key={i} className="vb-note">
              {l}
            </p>
          );
        const gap = v.speaker === '상' && i > firstIdx;
        return (
          <p key={i} className={`vb-line${gap ? ' vb-gap' : ''}${v.uncertain ? ' uncertain' : ''}`}>
            <strong className="vb-who">
              {v.label}
              {v.noText ?? v.no ?? ''}
            </strong>
            {v.text}
          </p>
        );
      })}
    </div>
  );
}

function VerbatimTable({ lines }: { lines: string[] }) {
  return (
    <table className="form-table verbatim-table">
      <tbody>
        {lines
          .filter((l) => l.trim())
          .map((l, i) => {
            const v = parseVerbatimLine(l);
            return v ? (
              <tr key={i} className={v.uncertain ? 'uncertain' : undefined}>
                <th>
                  {v.label}
                  {v.noText ?? v.no ?? ''}
                  {v.uncertain && <span className="small"> 확인</span>}
                </th>
                <td>{v.text}</td>
              </tr>
            ) : (
              <tr key={i}>
                <td colSpan={2}>{l}</td>
              </tr>
            );
          })}
      </tbody>
    </table>
  );
}

const isVerbatim = (b: DocBlock) =>
  b.kind === 'field' && isVerbatimLabel(b.label) && b.lines.some((l) => parseVerbatimLine(l));

function MetaCell({ label, value, span }: { label: string; value: string; span: number }) {
  return (
    <>
      <th>{label}</th>
      <td colSpan={span}>{value}</td>
    </>
  );
}

/** 칸별 고치기: 원문(저장되는 내용)을 꺼내고 저장하는 함수 */
export type FieldEdit = { body: (label: string) => string; save: (label: string, body: string) => void };

/** 칸 제목 옆 '고치기' → 그 칸만 바로 고친다 (문서 전체 편집으로 갈 필요 없음) */
function Editable({ label, edit, children }: { label: string; edit?: FieldEdit; children: ReactNode }) {
  const [draft, setDraft] = useState<string | null>(null);
  if (!edit) return <>{children}</>;
  if (draft === null)
    return (
      <>
        <button type="button" className="link small no-print field-edit" onClick={() => setDraft(edit.body(label))}>
          고치기
        </button>
        {children}
      </>
    );
  return (
    <div className="stack no-print">
      <textarea
        aria-label={`${label} 고치기`}
        value={draft}
        rows={Math.min(24, Math.max(4, draft.split('\n').length + 1))}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="row">
        <button
          type="button"
          className="primary"
          onClick={() => {
            edit.save(label, draft);
            setDraft(null);
          }}
        >
          저장
        </button>
        <button type="button" onClick={() => setDraft(null)}>
          취소
        </button>
      </div>
    </div>
  );
}

/** 문서 보기·인쇄: 상담기록지는 기관 양식처럼 칸 표, 축어록은 화자 | 발언 표 (HTML 주입 없음) */
export function DocView({
  text,
  type,
  edit,
}: {
  text: string;
  type: DocumentArtifact['documentType'];
  edit?: FieldEdit;
}) {
  const doc = parseDoc(text);
  return (
    <article className={`doc doc-${type}`}>
      {doc.title && <h2 className="doc-title">{doc.title}</h2>}
      {doc.meta.length > 0 && (
        <table className="form-table meta-table">
          <tbody>
            {Array.from({ length: Math.ceil(doc.meta.length / 2) }, (_, r) => doc.meta.slice(r * 2, r * 2 + 2)).map(
              (row, r) => (
                <tr key={r}>
                  {row.map((m, k) => (
                    <MetaCell
                      key={m.label}
                      label={m.label}
                      value={m.value}
                      span={row.length === 1 && k === 0 ? 3 : 1}
                    />
                  ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
      {type === 'session_record' ? (
        <table className="form-table record-table">
          <tbody>
            {doc.blocks.map((b, i) =>
              b.kind === 'field' ? (
                <tr key={i}>
                  <th>{b.label}</th>
                  <td>
                    <Editable label={b.label} edit={edit}>
                      <Lines lines={b.lines} />
                    </Editable>
                  </td>
                </tr>
              ) : (
                <tr key={i}>
                  <td colSpan={2}>{b.text}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      ) : (
        doc.blocks.map((b, i) =>
          b.kind === 'section' ? (
            <h3 key={i}>{b.text}</h3>
          ) : b.kind === 'para' ? (
            type === 'dialogue' && isDialogueRangeNote(b.text) ? null : (
              <p key={i}>{b.text}</p>
            )
          ) : type === 'dialogue' && isDialogueExtraField(b.label) ? null : (
            <section key={i} className={b.sub ? 'doc-field doc-sub' : 'doc-field'}>
              {/* 대화록: 여섯 구성 요소는 큰 제목 아래 바로 본문 (작성표·예시 양식) */}
              {type === 'dialogue' && isDialogueMainField(b.label) ? null : b.sub ? (
                <h5>{b.label}</h5>
              ) : (
                <h4>{b.label}</h4>
              )}
              <Editable label={b.label} edit={edit}>
                {isVerbatim(b) ? (
                  type === 'dialogue' ? (
                    <VerbatimDialogue lines={b.lines} />
                  ) : (
                    <VerbatimTable lines={b.lines} />
                  )
                ) : (
                  <Lines lines={b.lines} />
                )}
              </Editable>
            </section>
          ),
        )
      )}
    </article>
  );
}
